import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import { filters } from "components/Common/constants"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import TableShimmer from "components/Shared/TableShimmer"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { Badge, Card, CardBody, Col, Container, Row, Table } from "reactstrap"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import {
  getAllGiftCardsUsage,
  getAllGiftsCards,
  getGiftsCardList,
  getGiftsCardListByCardType,
} from "store/actions/giftCardAction"
import { getAllGiftCardCategoriesWithoutPagination } from "store/actions/giftCardCategoriesAction"

import formatDateTime from "components/Shared/formatDateTime"

const GiftCardUsage = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Gift Card Usage ")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const flatpickrRef = useRef(null)
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [dateRange, setDateRange] = useState({})
  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    giftCardsUsage,
    giftCardListByType,
  } = useSelector(state => state.GiftCard)

  const { categories } = useSelector(state => state.GiftCardCategories)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      38,
      "gift_card_usage_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    } else {
      fetchAllRecords()
      fetchGiftCardCategories()
    }
  }, [userRole])

  const fetchGiftCardCategories = () => {
    dispatch(getAllGiftCardCategoriesWithoutPagination())
  }

  const sortFilter = {
    name: "sortOrder",
    type: "select",
    options: [
      { label: t("Ascending"), value: 1 },
      { label: t("Descending"), value: 2 },
    ],
    placeholder: t("Descending"),
  }

  const giftCardFilter = {
    name: "giftCardId",
    type: "singleSelect",
    options: [
      ...giftCardListByType.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      })),
    ],
    placeholder: t("Select Gift Card"),
  }

  const categoryFilter = {
    name: "categoryId",
    type: "singleSelect",
    options: [
      ...categories.map(item => ({
        label: item.cardType,
        value: item._id,
      })),
    ],
    placeholder: t("Select Category"),
  }

  // Redeemed filter (dynamic)
  const redeemedFilter = {
    name: "redeemed",
    type: "singleSelect",
    options: [
      { label: t("All"), value: null },
      { label: t("Redeemed"), value: true },
      { label: t("Not Redeemed"), value: false },
    ],
    placeholder: t("Select Redeemed Status"),
  }

  const updatedFilters = [
    sortFilter,
    categoryFilter,
    giftCardFilter,
    redeemedFilter,
  ]

  const [searchTerm, setSearchTerm] = useState("")
  const [searchTerm2, setSearchTerm2] = useState("")
  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: 2,
    giftCardId: "",
    categoryId: "",
    receiverNumber: "",
    redeemed: null, // default to null, meaning no filter (All)
  })

  useEffect(() => {
    if (selectedFilters?.categoryId?.value) {
      const payload = {
        cardTypeId: selectedFilters.categoryId.value,
      }
      dispatch(getGiftsCardListByCardType(payload))
    }
  }, [selectedFilters.categoryId.value])

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleSearchChange2 = e => {
    setSearchTerm2(e.target.value)
  }

  const exportToCSV = async () => {
    const { giftCardId, redeemed, categoryId } = selectedFilters
    const updatedGiftCardId = giftCardId?.value || ""
    const updatedCategoryId = [categoryId?.value] || []
    const hasDateRange = dateRange?.min && dateRange?.max

    const payload = {
      type: "GIFT-CARD-USAGE",
      exportType: "csv",
      giftCardId: updatedGiftCardId,
      categoryId: updatedCategoryId,
      redeemed: redeemed?.value || null, // Send redeemed as null, true, or false
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      const fileName = hasDateRange
        ? t(`gift_card_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Gift Card Report Till Date")
      DownloadCSV(csvData, fileName)
    }
  }

  const handleExcelDownload = async () => {
    const { giftCardId, redeemed, categoryId } = selectedFilters
    const updatedGiftCardId = giftCardId?.value || ""
    const updatedCategoryId = [categoryId?.value] || []
    const hasDateRange = dateRange?.min && dateRange?.max

    const payload = {
      type: "GIFT-CARD-USAGE",
      exportType: "xlsx",
      giftCardId: updatedGiftCardId,
      categoryId: updatedCategoryId,
      redeemed: redeemed?.value || null, // Send redeemed as null, true, or false
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      const fileName = hasDateRange
        ? t(`gift_card_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Gift Card Report Till Date")
      DownloadXLSX(xlsxData, fileName)
    }
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue, // selectedValue will be true or false, not the entire object
    }))
  }

  const fetchAllRecords = () => {
    const { redeemed, giftCardId, categoryId } = selectedFilters
    const updatedGiftCardId = giftCardId?.value || ""
    const updatedCategoryId = categoryId?.value || ""

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      orderBy: 2,
      keyword: "",
      giftCardSearch: "",
      giftCardId: updatedGiftCardId,
      categoryId: updatedCategoryId,
      receiverNumber: "",
      userId: "",
      redeemed: redeemed?.value || null, // redeemed will be true or false, not an object
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    dispatch(getAllGiftCardsUsage(payload))
  }

  const getApiCall = (page = 1) => {
    const { sortOrder, giftCardId, redeemed, categoryId } = selectedFilters
    const updatedGiftCardId = giftCardId?.value || ""
    const updatedCategoryId = categoryId?.value || ""

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      giftCardSearch: searchTerm2 || "",
      giftCardId: updatedGiftCardId,
      categoryId: updatedCategoryId,
      receiverNumber: "",
      userId: "",
      redeemed: redeemed?.value,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    return dispatch(getAllGiftCardsUsage(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/giftcard-reports?page=${page}`)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const handleSearch = () => {
    getApiCall()
  }

  const onPageChange = page => {
    getApiCall(page)
  }

  const handleReset = () => {
    setSearchTerm("")
    setSearchTerm2("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      orderBy: 2,
      keyword: "",
      giftCardSearch: "",
      giftCardId: "",
      categoryId: "",
      receiverNumber: "",
      userId: "",
      redeemed: null,
      dateRange: {},
    }
    dispatch(getAllGiftCardsUsage(payload))
    dispatch({ type: "gift card/setGiftCardListByType", payload: [] })
    navigate(`/giftcard-reports?page=${1}`)
    setSelectedFilters({
      sortOrder: 2,
      giftCardId: "",
      categoryId: "",
      receiverNumber: "",
      redeemed: null, // Reset redeemed filter to null (All)
    })
    setDateRange({})
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear()
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />

        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Gift Card Reports")}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                isExportData={loading ? false : true}
              />
              <CardBody>
                <Filters
                  filters={updatedFilters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Sender Phone")}
                  isSecondSearch={true}
                  searchTerm2={searchTerm2}
                  handleSearchChange2={handleSearchChange2}
                  searchBy2={t("Gift Card Name")}
                  colSize={4}
                  dateRange={dateRange}
                  isRangeDate={true}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                />
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : giftCardsUsage.length === 0 ? (
                    <p className="text-center my-3">{t("No data found")}</p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th style={{ maxWidth: 50 }}>{t("S. No.")}</th>
                          <th>{t("Gift Card No")}</th>
                          <th>{t("Gift Card Name")}</th>
                          <th>{t("Category")}</th>
                          <th>{t("Redeemed")}</th>
                          <th>{t("Sender Phone Number")}</th>
                          <th>{t("Receiver Phone Number")}</th>
                              <th>{t("Amount")}</th>
                              <th>{t("Payment Method")}</th>
                              <th>{t("Payment Status")}</th>
                              <th>{t("Transaction Id")}</th>
                          {/* <th>{t("Message")}</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {giftCardsUsage.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() => {
                              navigate(
                                `/giftcard-reports/details?id=${item._id}`
                              )
                            }}
                            className="cursor-pointer"
                          >
                            <th scope="row">
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </th>
                            <td>{item?.giftCardNo ?? "-"}</td>
                            <td>
                              {language === "sa"
                                ? item.giftCardId?.nameAr
                                : item.giftCardId?.name || "-"}
                            </td>
                            <td>
                              {item.giftCardId?.cardType?.cardType || "-"}
                            </td>
                            <td>
                              {item.redeemed ? (
                                <Badge className="bg-success">
                                  {t("Redeemed")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Not Redeemed")}
                                </Badge>
                              )}
                            </td>
                            <td>
                              <p className="ltr w_fit">
                                {item?.senderId?.phone || "-"}
                              </p>
                            </td>
                            <td>
                              <p className="ltr w_fit">
                                {item?.receiverNumber || "-"}
                              </p>
                            </td>
                            <td>
                              {item?.amount ?? "-"} {item?.currency}
                            </td>
                            <td>
                              {item?.transactionDetails?.paymentMethod || "-"}
                            </td>
                            <td>
                              {item?.transactionDetails?.gatewayStatus || "-"}
                            </td>
                            <td>
                              {item?.transactionDetails?.transactionId || "-"}
                            </td>
                            {/* <td style={{ maxWidth: 230 }}>
                              <p className="in_2_line">{item?.message}</p>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && giftCardsUsage.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={giftCardsUsage}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default GiftCardUsage
